// SELF, ported from internal/wire/self.go.
//
// The one per-connection message: the bank the player is spending from, and what
// the tick decided about their last click.
//
//	u16 banked           clicks in the bank right now
//	u16 capacity         bank ceiling
//	u16 refillSeconds    whole seconds until the next click is banked
//	u8  hasLast          0 = no click has been judged on this connection yet
//	u8  verdict          core.Verdict
//	u32 seq              the client's own counter, echoed back
//
// The verdict is the point of the class. SPEC §3: reconciliation has three
// failure outcomes and they must look different — an illegal click flashes and
// returns the click to the bank, a lost click snaps back and reads as "someone
// beat you to it", a dropped click is refunded and says so. The enum is carried
// unmodified rather than collapsed into a boolean.
//
// hasLast is an explicit field rather than a sentinel verdict, because
// VerdictApplied is zero and a fresh connection would otherwise report a
// successful click nobody made.

import { Reader, Writer } from './bytes';
import { ClassSelf } from './classes';
import { MaxVerdict, verdictRefunds, type TickNo, type Verdict } from './core';
import { errBadBool, errVerdict } from './errors';

/** The fixed body width; SELF never varies in length. */
export const SelfBodySize = 2 + 2 + 2 + 1 + 1 + 4;

export interface Self {
  tick: TickNo;
  banked: number;
  capacity: number;
  /**
   * Whole seconds to the next banked click. Zero means the bank is full or a
   * click lands this second; the client counts down locally between SELF frames,
   * which are sent on change rather than every tick.
   */
  refillSeconds: number;
  /** False until a click from this connection has been judged. */
  hasLast: boolean;
  verdict: Verdict;
  /** The client's own click counter, echoed so an optimistic client can match a correction to the square it drew. */
  seq: number;
}

/**
 * Whether the last click's budget went back to the bank. Derived from the verdict
 * rather than carried on the wire, so the two can never disagree.
 */
export function selfRefunds(s: Self): boolean {
  return s.hasLast && verdictRefunds(s.verdict);
}

export function decodeSelf(b: Uint8Array): Self {
  const r = new Reader(b);
  const tick = r.header(ClassSelf);
  const banked = r.u16();
  const capacity = r.u16();
  const refillSeconds = r.u16();
  const has = r.u8();
  const verdict = r.u8();
  const seq = r.u32();
  r.done();
  if (has > 1) throw errBadBool();
  if (verdict > MaxVerdict) throw errVerdict();
  return {
    tick,
    banked,
    capacity,
    refillSeconds,
    hasLast: has === 1,
    verdict: verdict as Verdict,
    seq,
  };
}

/**
 * The encoder is server-side, and exists here only so a test can build a frame
 * the decoder is then run against without reaching for a fixture file.
 */
export function encodeSelf(s: Self): Uint8Array {
  if (s.verdict > MaxVerdict) throw errVerdict();
  return new Writer(9 + SelfBodySize)
    .header(ClassSelf, s.tick)
    .u16(s.banked)
    .u16(s.capacity)
    .u16(s.refillSeconds)
    .u8(s.hasLast ? 1 : 0)
    .u8(s.verdict)
    .u32(s.seq)
    .finish();
}
